import {ChangeEvent, useState, VoidFunctionComponent} from 'react'
import {Box, Button, Typography} from '@material-ui/core'
import {Check, CloudUploadOutlined} from '@material-ui/icons'
import {useFetchFromServer} from '../api/fetchHook'
import {v4 as uuidv4} from 'uuid'

const BASE_URL = process.env.PUBLIC_URL + '/api/issuesUpload'
const GET_UPLOAD_FILE_PRE_SIGNED_URL = BASE_URL + '/getPreSignedUrl'
const UPLOAD_ISSUES_URL = BASE_URL + '/upload'

export const UploadIssues: VoidFunctionComponent = () => {
    const [issuesFile, setIssuesFile] = useState<File | null>(null)
    const [state, setState] = useState<{
        message: string
        type: 'running' | 'error' | 'success'
    } | null>(null)

    const {fetchJsonFromServer, fetchFromServer} = useFetchFromServer()

    const onIssuesFileUpdated = (e: ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length > 1) return
        setIssuesFile(e.target.files[0])
    }

    const uploadFile = async (
        uploadId: string,
        file: File,
        type: 'issues' | 'summary',
    ) => {
        const {url} = await fetchJsonFromServer<{ url: string }>(
            GET_UPLOAD_FILE_PRE_SIGNED_URL,
            {
                method: 'POST',
                body: JSON.stringify({uploadId, type}),
                headers: {'Content-Type': 'application/json'},
            },
        )
        const response = await fetch(url, {method: 'PUT', body: file})
        if (!response.ok)
            throw new Error(`Failed uploading file using pre-signed url`)
    }

    const uploadIssues = async (uploadId: string) => {
        await fetchFromServer(UPLOAD_ISSUES_URL, {
            method: 'POST',
            body: JSON.stringify({uploadId}),
            headers: {'Content-Type': 'application/json'},
        })
    }

    const onSubmit = async () => {
        if (!issuesFile) {
            setState({
                message: 'please supply issues file and summary file',
                type: 'error',
            })
            return
        }
        try {
            const uploadId = uuidv4()
            setState({message: 'uploading files', type: 'running'})
            await Promise.all([
                uploadFile(uploadId, issuesFile, 'issues'),
            ])
            setState({message: 'uploading issues', type: 'running'})
            await uploadIssues(uploadId)
            setState({message: 'uploaded issues successfully', type: 'success'})
            setIssuesFile(null)
        } catch (e) {
            setState({message: (e as Error).message, type: 'error'})
        }
    }

    return (
        <Box
            margin={1}
            display='flex'
            flexDirection='column'
            alignItems='flex-start'
            style={{gap: 3}}
        >
            <Button
                variant='contained'
                component='label'
                size='large'
                color='secondary'
                startIcon={<CloudUploadOutlined/>}
                endIcon={!issuesFile ? undefined : <Check/>}
            >
                Select Analyzed Issues File
                <input
                    type='file'
                    hidden
                    accept='application/pdf'
                    onChange={onIssuesFileUpdated}
                />
            </Button>
            <Button
                variant='contained'
                color='primary'
                disabled={!issuesFile || state?.type === 'running'}
                onClick={onSubmit}
            >
                Upload
            </Button>
            {!!state && (
                <Typography color={state.type === 'error' ? 'error' : 'textPrimary'}>
                    {state.message}
                </Typography>
            )}
        </Box>
    )
}
